import { Check } from "lucide-react";
import * as React from "react";

import { cn } from "@/shared/lib/utils";

export interface ColocaStepperStep {
  /** Short step label — e.g. "Company data". Keep to 2–3 words. */
  label: React.ReactNode;
}

export interface ColocaStepperProps extends React.ComponentPropsWithoutRef<"ol"> {
  /** Ordered list of steps. A stepper needs at least two. */
  steps: ColocaStepperStep[];
  /** Zero-based index of the active step. Steps before it render as completed (check icon). */
  currentStep: number;
  /** Accessible name for the stepper navigation — e.g. "Wizard progress". */
  "aria-label"?: string;
}

/**
 * Horizontal progress stepper for multi-step modals and wizards.
 *
 * Completed steps show a check, the active step is highlighted with the brand color, and the connector line
 * fills as the user advances (animated, 250 ms). Purely presentational: the parent owns the step state and
 * navigation logic — pair it with footer buttons (Back / Continue) that move `currentStep`.
 *
 * @summary horizontal wizard progress with completed checks and animated connectors
 */
export const ColocaStepper = React.forwardRef<HTMLOListElement, ColocaStepperProps>(function ColocaStepper(
  { steps, currentStep, className, ...rest },
  ref,
) {
  return (
    <ol ref={ref} className={cn("flex items-center gap-2", className)} {...rest}>
      {steps.map((step, index) => {
        const isCompleted = index < currentStep;
        const isActive = index === currentStep;
        return (
          <li
            key={index}
            className="flex min-w-0 items-center gap-2"
            aria-current={isActive ? "step" : undefined}
          >
            {index > 0 && (
              <span
                aria-hidden="true"
                className="relative h-px w-6 shrink-0 overflow-hidden rounded-full bg-border sm:w-10"
              >
                <span
                  className={cn(
                    "absolute inset-0 origin-left bg-primary transition-transform duration-250",
                    isCompleted || isActive ? "scale-x-100" : "scale-x-0",
                  )}
                />
              </span>
            )}
            <span
              aria-hidden="true"
              className={cn(
                "flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-colors",
                isCompleted && "bg-primary text-primary-foreground",
                isActive && "bg-primary/10 text-primary ring-1 ring-primary/40",
                !isCompleted && !isActive && "bg-muted text-muted-foreground",
              )}
            >
              {isCompleted ? <Check className="size-3.5" /> : index + 1}
            </span>
            <span
              className={cn(
                "truncate text-xs font-medium",
                isActive ? "text-foreground" : "text-muted-foreground",
                !isActive && "hidden sm:inline",
              )}
            >
              {step.label}
            </span>
          </li>
        );
      })}
    </ol>
  );
});
